/**
 * Phase 1.5d polish — ArrowIcon
 *
 * Thin inline SVG arrow. Solves the iOS Safari / Android Chrome bug where
 * Unicode arrow code-points (↗ ↘ → ← etc.) are substituted by the OS
 * Apple Color Emoji font and render as bulky blue-button emoji glyphs.
 *
 * Usage:
 *   <ArrowIcon />                           // defaults to up-right, 1em, inherits color
 *   <ArrowIcon direction="right" />
 *   <ArrowIcon direction="left" />
 *   <ArrowIcon direction="down" color="var(--accent)" size="1.2em" />
 *
 * The SVG inherits the surrounding text's `font-size` (via `1em`) and
 * `color` (via `currentColor`) by default, so it drops into any coral /
 * ink / paper button without further styling.
 */
import React from "react";

const ROTATIONS = {
    right: 0,
    "down-right": 45,
    down: 90,
    "down-left": 135,
    left: 180,
    "up-left": 225,
    up: 270,
    "up-right": 315,
};

export function ArrowIcon({
    direction = "up-right",
    className = "",
    size = "1em",
    color = "currentColor",
    "aria-hidden": ariaHidden = true,
    ...rest
}) {
    const deg = ROTATIONS[direction] ?? 315;
    return (
        <svg
            viewBox="0 0 24 24"
            width={size}
            height={size}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
                transform: `rotate(${deg}deg)`,
                display: "inline-block",
                verticalAlign: "-0.125em",
                flexShrink: 0,
            }}
            className={`arrow-icon ${className}`.trim()}
            aria-hidden={ariaHidden}
            focusable="false"
            {...rest}
        >
            {/* Base: right-pointing arrow (→). All other directions rotate. */}
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="13 6 19 12 13 18" />
        </svg>
    );
}

export default ArrowIcon;
